import React from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Button,
    CircularProgress,
    Box,
    Snackbar,
    Alert
} from '@mui/material';
import { useState } from 'react';
import { useContent } from '../../../../context/ContentContext';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const WithdrawSaldo = ({ dialogOpen, formData, setFormData, handleCloseDialog }) => {
    const { createPaymentRequest, loading, error, setError, RequestWDStatus, setRequestWDStatus, setLoading } = useContent(); // Destructure context functions and states
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check if it's a mobile screen size

    // Handle user input changes
    const handleValueChange = (e) => {
        let value = e.target.value;

        // Remove non-numeric characters
        value = value.replace(/[^\d]/g, '');

        // Convert string to number
        const numericValue = parseInt(value, 10);

        // Update form data with numeric value
        setFormData({ ...formData, saldo: isNaN(numericValue) ? 0 : numericValue });
    };

    // Format number for display (without currency symbol)
    const formatCurrency = (number) => {
        if (isNaN(number)) return ''; // Prevent formatting NaN values
        return number.toLocaleString('id-ID', {
            minimumFractionDigits: 0, // Disable decimals
        });
    };

    // Handle the withdrawal submit
    const handleSubmit = async () => {
        // Log the current Payment RequestWDStatus from context before any action
        console.log('Current Payment RequestWDStatus before submission:', RequestWDStatus);

        // Check if a request is already in progress (based on RequestWDStatus from context)
        if (RequestWDStatus === true) {
            console.log('A withdrawal request is already in progress.');
            return; // Exit early if the request is already being processed
        }

        // Check if the balance is below the minimum withdrawal amount
        if (formData.saldo < 10000) {
            console.log('Minimum withdrawal amount is Rp. 10.000');
            setAlert({ open: true, severity: 'error', message: 'Minimum withdrawal amount is Rp. 10.000' });
            return;
        }

        try {
            // Log the current RequestWDStatus before sending the request
            console.log('Payment RequestWDStatus before sending request:', RequestWDStatus);

            // Send the payment request to the API
            await createPaymentRequest({ nominal: formData.saldo }); // Send `nominal`, not `saldo`

            // Log the current RequestWDStatus after the API call
            console.log('Payment RequestWDStatus after API call:', RequestWDStatus);

            // Check if there was an error after the API call
            if (RequestWDStatus === 'error') {
                console.error('Error during payment request:', error);

                // Show error message in an alert (using the error from context directly)
                setAlert({ open: true, severity: 'error', message: error?.message || 'Error processing the request' });
                return;  // Exit early if there is an error
            }

            // If the request was successful
            if (RequestWDStatus === 'success') {
                // Show success message in an alert
                setAlert({ open: true, severity: 'success', message: 'Withdrawal request processed successfully' });
            }

        } catch (err) {
            console.error('Error while submitting withdrawal request:', err.message);

            // Show error message in an alert (using the error from catch directly)
            setAlert({ open: true, severity: 'error', message: err.message || 'Unknown error occurred' });
        } finally {
            // Reset loading state
            setLoading(false);
            console.log('Loading state set to false');
            handleCloseDialog();
        }
    };

    const [alert, setAlert] = useState({ open: false, severity: '', message: '' });

    return (
        <>
            <Dialog open={dialogOpen} onClose={handleCloseDialog}>
                <DialogTitle>Withdraw Saldo</DialogTitle>
                <DialogContent>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* Display general error message */}
                            {error?.general && (
                                <Box color="red" mb={2}>
                                    {error.general}
                                </Box>
                            )}

                            <Box width={isMobile ? '100%' : '700px'} mx="auto">
                                <TextField
                                    label="Amount (IDR)"
                                    name="saldo"
                                    value={formData.saldo === 0 ? '' : formatCurrency(formData.saldo)} // Show formatted value, empty if 0
                                    onChange={handleValueChange} // Handle value change
                                    fullWidth={isMobile} // Full width for mobile
                                    margin="normal"
                                    helperText={typeof error === 'string' ? error : (error && error.message ? error.message : '')} // Ensure helperText is a string or empty
                                />
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Box width={isMobile ? '100%' : '700px'} mx="auto">
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            color="primary"
                            fullWidth={isMobile}
                            disabled={loading} // Disable button when loading
                        >
                            {loading ? 'Processing...' : 'Withdraw'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={() => setAlert({ ...alert, open: false })}
            >
                <Alert
                    onClose={() => setAlert({ ...alert, open: false })}
                    severity={alert.severity}
                    sx={{ width: '100%' }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default WithdrawSaldo;
